import { createSlice } from '@reduxjs/toolkit';

import type { ArbitroWorkspaceState, PainelNotificacaoItem } from '../types';
import {
  consultarRespostaPainel,
  criarAndamentoPainel,
  emitirTermoImparcialidadePainel,
  emitirTermosLotePainel,
  listarAndamentosPainel,
  listarCertificadosPainel,
  listarDocumentosPainel,
  listarNotificacoesPainel,
  listarPendenciasPainel,
  listarProcessosArbitro,
  listarProcessosPainel,
  limparErroRecusaNomeacao,
  listarTermosPendentesPainel,
  marcarAvisoLidoPainel,
  marcarAvisosLidosPainel,
  obterCertificadoAcessosPainel,
  obterDashboardPainel,
  obterFinanceiroPainel,
  obterProcessoPainel,
  publicarDocumentoEmissaoPainel,
  recusarNomeacaoPainel,
  registrarReciboResposta,
} from './actions';
import { addModelosEmissaoReducers, modelosEmissaoInitialState } from './modelosEmissaoReducers';

// O detalhe carregado, e só se for mesmo o do processo da resposta: entre a saída de um
// processo e a chegada do detalhe do outro, `detalheItem` ainda é o antigo — sem esta guarda,
// uma lista que chegasse nessa janela carimbaria seu total no processo errado.
const detalheDoProcesso = (
  state: ArbitroWorkspaceState,
  processoId: string,
): ArbitroWorkspaceState['detalheItem'] =>
  state.detalheItem?.id === processoId ? state.detalheItem : null;

// Os contadores das abas seguem as listas, não o número que veio no GET do detalhe: as três
// respostas chegam em ordem imprevisível, e a do detalhe pode ser anterior a um andamento que
// já está na timeline (a intimação da sentença nasce assíncrona, no motor de flows).
const alinharContadoresComAsListas = (state: ArbitroWorkspaceState): void => {
  if (!state.detalheItem) {
    return;
  }
  if (state.andamentosProcessoId === state.detalheItem.id) {
    state.detalheItem.andamentosCount = state.andamentos.length;
  }
  if (state.documentosProcessoId === state.detalheItem.id) {
    state.detalheItem.documentosCount = state.documentos.length;
  }
};

// Responder um concluso (andamento simples ou publicação de documento) o resolve no backend;
// reflete no feed na hora para o sino/central não esperarem o próximo polling.
const resolverPendenciaNoFeed = (itens: PainelNotificacaoItem[], pendenciaId: string): void => {
  const item = itens.find((i) => i.id === pendenciaId && i.natureza === 'PENDENCIA');
  if (item) {
    item.resolvido = true;
  }
};

const initialState: ArbitroWorkspaceState = {
  modelosEmissao: modelosEmissaoInitialState,
  processos: [],
  isLoadingProcessos: false,
  errorProcessos: null,
  notificacoesPainel: [],
  isLoadingNotificacoesPainel: false,
  painelItems: [],
  painelPaginacao: { page: 1, limit: 20, total: 0, totalPages: 1 },
  painelFacetas: { situacoes: [], status: [] },
  // Começa em loading: a lista sempre dispara um fetch na montagem. Evita o flash de
  // "nenhum resultado" no F5 (antes do .pending do primeiro fetch ligar o loading).
  isLoadingPainel: true,
  errorPainel: null,
  detalheItem: null,
  isLoadingDetalhe: false,
  errorDetalhe: null,
  detalheNaoEncontrado: false,
  andamentos: [],
  andamentosProcessoId: null,
  andamentosRequestId: null,
  andamentosSolicitadoProcessoId: null,
  isLoadingAndamentos: false,
  errorAndamentos: null,
  isCriandoAndamento: false,
  andamentoCriacaoRequestId: null,
  errorCriarAndamento: null,
  documentos: [],
  documentosProcessoId: null,
  documentosRequestId: null,
  isLoadingDocumentos: false,
  errorDocumentos: null,
  certificados: [],
  certificadosProcessoId: null,
  isLoadingCertificados: false,
  errorCertificados: null,
  certificadoAcessos: null,
  isLoadingCertificadoAcessos: false,
  errorCertificadoAcessos: null,
  isEmitindoTermo: false,
  errorEmitirTermo: null,
  isRecusandoNomeacao: false,
  errorRecusarNomeacao: null,
  termosPendentes: [],
  isLoadingTermosPendentes: false,
  isEmitindoLote: false,
  pendencias: [],
  isLoadingPendencias: false,
  pendenciasProcessoId: null,
  pendenciasRequestId: null,
  errorPendencias: null,
  respostasPendencias: {},
  consultasRespostasRequestIds: {},
  financeiroResumo: null,
  financeiroItens: [],
  isLoadingFinanceiro: false,
  dashboard: null,
  isLoadingDashboard: false,
  errorDashboard: null,
};

const arbitroSlice = createSlice({
  name: 'arbitro',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    addModelosEmissaoReducers(builder);
    builder
      .addCase(registrarReciboResposta, (state, action) => {
        const { id, descricao, recibo } = action.payload;
        delete state.consultasRespostasRequestIds[`${id}:${recibo.pendenciaId}`];
        state.respostasPendencias[id] ??= {};
        state.respostasPendencias[id][recibo.pendenciaId] = {
          pendenciaId: recibo.pendenciaId,
          descricao,
          recibo,
          resolvida: recibo.statusAtual === 'RESOLVIDA',
        };
        if (recibo.statusAtual === 'RESOLVIDA')
          resolverPendenciaNoFeed(state.notificacoesPainel, recibo.pendenciaId);
      })
      .addCase(consultarRespostaPainel.pending, (state, action) => {
        const { id, pendenciaId } = action.meta.arg;
        state.consultasRespostasRequestIds[`${id}:${pendenciaId}`] = action.meta.requestId;
      })
      .addCase(consultarRespostaPainel.fulfilled, (state, action) => {
        const { id, pendenciaId } = action.meta.arg;
        if (state.consultasRespostasRequestIds[`${id}:${pendenciaId}`] !== action.meta.requestId)
          return;
        delete state.consultasRespostasRequestIds[`${id}:${pendenciaId}`];
        const recibo = action.payload;
        if (!recibo) return;
        state.respostasPendencias[id] ??= {};
        if (recibo.statusAtual === 'ABERTA') {
          delete state.respostasPendencias[id][pendenciaId];
          return;
        }
        const anterior = state.respostasPendencias[id][pendenciaId];
        state.respostasPendencias[id][pendenciaId] = {
          pendenciaId,
          descricao: anterior?.descricao ?? '',
          recibo,
          resolvida: recibo.statusAtual === 'RESOLVIDA',
        };
        if (recibo.statusAtual === 'RESOLVIDA')
          resolverPendenciaNoFeed(state.notificacoesPainel, pendenciaId);
      })
      .addCase(consultarRespostaPainel.rejected, (state, action) => {
        const chave = `${action.meta.arg.id}:${action.meta.arg.pendenciaId}`;
        if (state.consultasRespostasRequestIds[chave] === action.meta.requestId)
          delete state.consultasRespostasRequestIds[chave];
      })
      .addCase(listarProcessosArbitro.pending, (state) => {
        state.isLoadingProcessos = true;
        state.errorProcessos = null;
      })
      .addCase(listarProcessosArbitro.fulfilled, (state, action) => {
        state.isLoadingProcessos = false;
        state.processos = action.payload;
      })
      .addCase(listarProcessosArbitro.rejected, (state, action) => {
        state.isLoadingProcessos = false;
        state.errorProcessos = action.payload as string;
      })

      .addCase(listarNotificacoesPainel.pending, (state) => {
        state.isLoadingNotificacoesPainel = true;
      })
      .addCase(listarNotificacoesPainel.fulfilled, (state, action) => {
        state.isLoadingNotificacoesPainel = false;
        state.notificacoesPainel = action.payload.itens;
      })
      .addCase(listarNotificacoesPainel.rejected, (state) => {
        state.isLoadingNotificacoesPainel = false;
      })

      // Optimistic (RF4): o aviso lido some dos ativos e vai para resolvidos na hora; o
      // próximo fetch reconcilia com o backend (resolvidoEm real).
      .addCase(marcarAvisoLidoPainel.fulfilled, (state, action) => {
        const aviso = state.notificacoesPainel.find(
          (item) => item.id === action.payload.recipienteId,
        );
        if (aviso) {
          aviso.lida = true;
          aviso.resolvido = true;
        }
      })
      .addCase(marcarAvisosLidosPainel.fulfilled, (state) => {
        for (const item of state.notificacoesPainel) {
          if (item.natureza === 'AVISO' && !item.resolvido) {
            item.lida = true;
            item.resolvido = true;
          }
        }
      })

      .addCase(listarProcessosPainel.pending, (state) => {
        state.isLoadingPainel = true;
        state.errorPainel = null;
      })
      .addCase(listarProcessosPainel.fulfilled, (state, action) => {
        state.isLoadingPainel = false;
        state.painelItems = action.payload.items;
        state.painelPaginacao = action.payload.pagination;
        state.painelFacetas = action.payload.filtros;
      })
      .addCase(listarProcessosPainel.rejected, (state, action) => {
        state.isLoadingPainel = false;
        state.errorPainel = action.payload as string;
      })

      .addCase(obterProcessoPainel.pending, (state) => {
        state.isLoadingDetalhe = true;
        state.errorDetalhe = null;
        state.detalheNaoEncontrado = false;
        state.detalheItem = null;
      })
      .addCase(obterProcessoPainel.fulfilled, (state, action) => {
        state.isLoadingDetalhe = false;
        if (action.payload.kind === 'not_found') {
          state.detalheNaoEncontrado = true;
        } else {
          state.detalheItem = action.payload.detalhe;
          alinharContadoresComAsListas(state);
        }
      })
      .addCase(obterProcessoPainel.rejected, (state, action) => {
        state.isLoadingDetalhe = false;
        state.errorDetalhe = action.payload as string;
      })

      .addCase(listarAndamentosPainel.pending, (state, action) => {
        state.andamentosRequestId = action.meta.requestId;
        state.andamentosSolicitadoProcessoId = action.meta.arg;
        state.isLoadingAndamentos = true;
        state.errorAndamentos = null;
      })
      // A lista manda no badge da aba: ela é a leitura mais nova das duas, e o contador veio do
      // GET do detalhe, que pode ser anterior a um andamento criado fora desta tela (a
      // intimação da sentença, que o motor de flows publica depois da resposta da publicação).
      .addCase(listarAndamentosPainel.fulfilled, (state, action) => {
        if (state.andamentosRequestId && state.andamentosRequestId !== action.meta.requestId)
          return;
        state.isLoadingAndamentos = false;
        state.andamentos = action.payload;
        state.andamentosProcessoId = action.meta.arg;
        const detalhe = detalheDoProcesso(state, action.meta.arg);
        if (detalhe) {
          detalhe.andamentosCount = action.payload.length;
        }
      })
      .addCase(listarAndamentosPainel.rejected, (state, action) => {
        if (state.andamentosRequestId && state.andamentosRequestId !== action.meta.requestId)
          return;
        state.isLoadingAndamentos = false;
        state.errorAndamentos = action.payload as string;
      })

      .addCase(criarAndamentoPainel.pending, (state, action) => {
        state.andamentoCriacaoRequestId = action.meta.requestId;
        // Um POST novo invalida consultas anteriores, que poderiam apagar sua chave ou recibo.
        if (action.meta.arg.pendenciaId)
          delete state.consultasRespostasRequestIds[
            `${action.meta.arg.id}:${action.meta.arg.pendenciaId}`
          ];
        state.isCriandoAndamento = true;
        state.errorCriarAndamento = null;
      })
      .addCase(criarAndamentoPainel.fulfilled, (state, action) => {
        if (
          state.andamentoCriacaoRequestId &&
          state.andamentoCriacaoRequestId !== action.meta.requestId
        )
          return;
        state.andamentoCriacaoRequestId = null;
        state.isCriandoAndamento = false;
        if (
          action.payload &&
          (!state.andamentosProcessoId || state.andamentosProcessoId === action.meta.arg.id)
        ) {
          state.andamentos = action.payload;
          state.andamentosProcessoId = action.meta.arg.id;
        }
        const detalhe = detalheDoProcesso(state, action.meta.arg.id);
        if (detalhe && action.payload) {
          detalhe.andamentosCount = action.payload.length;
          // O anexo do andamento também entra nos autos como documento (RF4), mas quem sobe
          // aquele contador é a releitura da lista, que o thunk dispara: somar aqui os
          // arquivos enviados brigaria com ela — as duas respostas correm em paralelo, e na
          // ordem em que a lista (já com os anexos) chega primeiro o total ficava dobrado.
          // "Atualizado há…" do header reflete o andamento recém-criado (mais recente = [0]),
          // sem refetch. O backend também bumpa dataAtualizacao do processo na mesma tx.
          detalhe.dataAtualizacao = action.payload[0]?.dataHora ?? detalhe.dataAtualizacao;
        }
        if (action.meta.arg.pendenciaId) {
          const { id, pendenciaId, descricao } = action.meta.arg;
          state.respostasPendencias[id] ??= {};
          state.respostasPendencias[id][pendenciaId] ??= {
            pendenciaId,
            descricao,
            resolvida: false,
          };
        }
      })
      .addCase(criarAndamentoPainel.rejected, (state, action) => {
        if (state.andamentoCriacaoRequestId !== action.meta.requestId) return;
        state.andamentoCriacaoRequestId = null;
        state.isCriandoAndamento = false;
        state.errorCriarAndamento = action.payload as string;
      })

      .addCase(listarDocumentosPainel.pending, (state, action) => {
        state.documentosRequestId = action.meta.requestId;
        state.isLoadingDocumentos = true;
        state.errorDocumentos = null;
      })
      .addCase(listarDocumentosPainel.fulfilled, (state, action) => {
        if (state.documentosRequestId && state.documentosRequestId !== action.meta.requestId)
          return;
        state.isLoadingDocumentos = false;
        state.documentos = action.payload;
        state.documentosProcessoId = action.meta.arg;
        const detalhe = detalheDoProcesso(state, action.meta.arg);
        if (detalhe) {
          detalhe.documentosCount = action.payload.length;
        }
      })
      .addCase(listarDocumentosPainel.rejected, (state, action) => {
        if (state.documentosRequestId && state.documentosRequestId !== action.meta.requestId)
          return;
        state.isLoadingDocumentos = false;
        state.errorDocumentos = action.payload as string;
      })

      .addCase(listarCertificadosPainel.pending, (state) => {
        state.isLoadingCertificados = true;
        state.errorCertificados = null;
      })
      .addCase(listarCertificadosPainel.fulfilled, (state, action) => {
        state.isLoadingCertificados = false;
        state.certificados = action.payload;
        state.certificadosProcessoId = action.meta.arg;
      })
      .addCase(listarCertificadosPainel.rejected, (state, action) => {
        state.isLoadingCertificados = false;
        state.errorCertificados = action.payload as string;
      })

      .addCase(obterCertificadoAcessosPainel.pending, (state) => {
        state.isLoadingCertificadoAcessos = true;
        state.errorCertificadoAcessos = null;
        // Sem limpar, ao trocar de processo na mesma rota o "Baixar PDF" pedia o PDF do novo
        // processo com o número do anterior no nome do arquivo (review do web#1190).
        state.certificadoAcessos = null;
      })
      .addCase(obterCertificadoAcessosPainel.fulfilled, (state, action) => {
        state.isLoadingCertificadoAcessos = false;
        state.certificadoAcessos = action.payload;
      })
      .addCase(obterCertificadoAcessosPainel.rejected, (state, action) => {
        state.isLoadingCertificadoAcessos = false;
        state.errorCertificadoAcessos = action.payload as string;
      })

      .addCase(emitirTermoImparcialidadePainel.pending, (state) => {
        state.isEmitindoTermo = true;
        state.errorEmitirTermo = null;
      })
      // O termo publicado entra nos autos, então o detalhe relido volta com os contadores já
      // incrementados enquanto as listas ainda são o retrato anterior. Realinhar aqui mantém
      // badge e aba dizendo a mesma coisa até as releituras (que o `emitirTermo` dispara)
      // chegarem com o andamento e o documento novos.
      .addCase(emitirTermoImparcialidadePainel.fulfilled, (state, action) => {
        state.isEmitindoTermo = false;
        if (action.payload.kind === 'ok') {
          state.detalheItem = action.payload.detalhe;
          alinharContadoresComAsListas(state);
        }
      })
      .addCase(emitirTermoImparcialidadePainel.rejected, (state, action) => {
        state.isEmitindoTermo = false;
        state.errorEmitirTermo = action.payload as string;
      })

      .addCase(recusarNomeacaoPainel.pending, (state) => {
        state.isRecusandoNomeacao = true;
        state.errorRecusarNomeacao = null;
      })
      // Quem recusou perde o acesso ao processo: o detalhe não é relido (daria 404); a tela sai
      // para o painel, onde o processo já não aparece.
      .addCase(recusarNomeacaoPainel.fulfilled, (state) => {
        state.isRecusandoNomeacao = false;
      })
      .addCase(recusarNomeacaoPainel.rejected, (state, action) => {
        state.isRecusandoNomeacao = false;
        state.errorRecusarNomeacao = action.payload ?? 'Erro ao recusar a nomeação';
      })
      .addCase(limparErroRecusaNomeacao, (state) => {
        state.errorRecusarNomeacao = null;
      })

      .addCase(listarTermosPendentesPainel.pending, (state) => {
        state.isLoadingTermosPendentes = true;
      })
      .addCase(listarTermosPendentesPainel.fulfilled, (state, action) => {
        state.isLoadingTermosPendentes = false;
        state.termosPendentes = action.payload;
      })
      .addCase(listarTermosPendentesPainel.rejected, (state) => {
        state.isLoadingTermosPendentes = false;
      })

      .addCase(emitirTermosLotePainel.pending, (state) => {
        state.isEmitindoLote = true;
      })
      .addCase(emitirTermosLotePainel.fulfilled, (state, action) => {
        state.isEmitindoLote = false;
        state.termosPendentes = action.payload;
      })
      .addCase(emitirTermosLotePainel.rejected, (state) => {
        state.isEmitindoLote = false;
      })

      .addCase(publicarDocumentoEmissaoPainel.fulfilled, (state, action) => {
        if (action.meta.arg.body.pendenciaId) {
          resolverPendenciaNoFeed(state.notificacoesPainel, action.meta.arg.body.pendenciaId);
        }
      })

      .addCase(listarPendenciasPainel.pending, (state, action) => {
        if (state.pendenciasProcessoId !== action.meta.arg) state.pendencias = [];
        state.pendenciasProcessoId = action.meta.arg;
        state.pendenciasRequestId = action.meta.requestId;
        state.isLoadingPendencias = true;
        state.errorPendencias = null;
      })
      .addCase(listarPendenciasPainel.fulfilled, (state, action) => {
        if (state.pendenciasRequestId !== action.meta.requestId) return;
        state.isLoadingPendencias = false;
        state.pendencias = action.payload;
        state.errorPendencias = null;
        for (const resposta of Object.values(state.respostasPendencias[action.meta.arg] ?? {})) {
          if (resposta.recibo) continue;
          const continuaAtiva = action.payload.some(
            (pendencia) => pendencia.id === resposta.pendenciaId,
          );
          if (resposta.resolvida && continuaAtiva) {
            delete state.respostasPendencias[action.meta.arg][resposta.pendenciaId];
            continue;
          }
          resposta.resolvida = !continuaAtiva;
          if (resposta.resolvida)
            resolverPendenciaNoFeed(state.notificacoesPainel, resposta.pendenciaId);
        }
      })
      .addCase(listarPendenciasPainel.rejected, (state, action) => {
        if (state.pendenciasRequestId !== action.meta.requestId) return;
        state.isLoadingPendencias = false;
        state.errorPendencias = action.payload ?? 'Não foi possível conferir as pendências.';
      })

      .addCase(obterDashboardPainel.pending, (state) => {
        state.isLoadingDashboard = true;
        state.errorDashboard = null;
      })
      .addCase(obterDashboardPainel.fulfilled, (state, action) => {
        state.isLoadingDashboard = false;
        state.dashboard = action.payload;
      })
      .addCase(obterDashboardPainel.rejected, (state, action) => {
        state.isLoadingDashboard = false;
        state.errorDashboard = action.payload as string;
      })

      .addCase(obterFinanceiroPainel.pending, (state) => {
        state.isLoadingFinanceiro = true;
      })
      .addCase(obterFinanceiroPainel.fulfilled, (state, action) => {
        state.isLoadingFinanceiro = false;
        state.financeiroResumo = action.payload.resumo;
        state.financeiroItens = action.payload.itens;
      })
      .addCase(obterFinanceiroPainel.rejected, (state) => {
        state.isLoadingFinanceiro = false;
      });
  },
});

export default arbitroSlice.reducer;
